import { Box } from '@mui/material'
import { DeleteActionButton, EditActionButton } from '../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import {
  useDeleteWorkforceTypeMutation,
  useGetWorkforceTypesByTenantIdQuery,
} from '../../app/redux-fetch/apiUser'
import AddButton from '../../components/Buttons/AddButton'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import { useConfirmDialogContext } from '../../components/ConfirmDialog'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../components/Typographies/TableTitleTypography'
import YesNoTypography from '../../components/YesNoTypograpphy/YesNoTypography'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import WorkforceTypeForm from './WorkforceTypeForm'
import { messages } from './messages'
import { IWorkforceType } from './types'

const WorkforceTypeTable = () => {
  const { formatMessage } = useIntl()
  const { tenantId } = useLoggedInUser()
  const [selectedId, setSelectedId] = useState(-1)
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const [deleteWorkforceType] = useDeleteWorkforceTypeMutation()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const {
    data: workforceTypes = [],
    isFetching,
    isSuccess,
  } = useGetWorkforceTypesByTenantIdQuery({ tenantId })

  const columns: MRTColumnDef<IWorkforceType>[] = [
    {
      accessorKey: 'name',
      header: formatMessage(messages.name),
    },
    {
      accessorKey: 'description',
      header: formatMessage(messages.description),
    },
    {
      accessorKey: 'inUse',
      header: formatMessage(messages.inUse),
      Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
    },
  ]

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: formatMessage(messages.deleteWorkforceTypeTitle),
      message: formatMessage(messages.deleteWorkforceTypeDescription),
      confirmText: formatMessage(messages.deleteBtn),
      cancelText: formatMessage(messages.cancelBtn),
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      deleteWorkforceType({ tenantId, body: { id } })
        .unwrap()
        .then(() => {
          enqueueSuccess(messages.workforceTypeDeleted)
        })
        .catch(() => {
          enqueueFailure(messages.workforceTypeNotDeleted)
        })
    }
  }

  return (
    <>
      <SideModalWrapper
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        headerLabel={formatMessage(messages.createEditWorkforceType)}
      >
        <WorkforceTypeForm
          workforceType={{
            tenantId,
            name: '',
            description: '',
          }}
          handleClose={() => setCreateOpen(false)}
          isOpen={createOpen}
        />
      </SideModalWrapper>

      <SideModalWrapper
        open={editOpen}
        onClose={() => setEditOpen(false)}
        headerLabel={formatMessage(messages.createEditWorkforceType)}
      >
        <WorkforceTypeForm
          workforceType={
            workforceTypes.find((wt) => wt.id === selectedId) ?? {
              tenantId,
              name: '',
              description: '',
            }
          }
          handleClose={() => setEditOpen(false)}
          isOpen={editOpen}
        />
      </SideModalWrapper>

      <Box>
        <TableTitleTypography>
          {formatMessage(messages.workforceTypeTableTitle)}
        </TableTitleTypography>
      </Box>

      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          density: 'comfortable',
          sorting: [{ id: 'name', desc: false }],
        }}
        rightHeadingComponent={
          <AddButton
            text={formatMessage(messages.newWorkforceTypeBtn)}
            onClick={() => setCreateOpen(true)}
          />
        }
        state={{
          isLoading: isFetching || !isSuccess,
        }}
        columns={columns}
        data={workforceTypes}
        getRowId={(row) => row.id as any}
        enableEditing
        enableRowActions
        positionActionsColumn='last'
        renderRowActions={({ row }) => (
          <Box sx={{ display: 'flex' }}>
            <EditActionButton
              onClick={() => {
                setSelectedId(row.original.id ?? -1)
                setEditOpen(true)
              }}
            />
            <ConditionalRender condition={!row.original.inUse}>
              <DeleteActionButton
                onClick={() => handleDelete(row.original.id ?? -1)}
              />
            </ConditionalRender>
          </Box>
        )}
      />
    </>
  )
}

export default WorkforceTypeTable
